import { useCallback, useState } from 'react';
import { tightPunctuation } from '../../utils/format';
import { t } from '../../i18n';

/**
 * Confirmation d'un geste qu'on ne peut pas defaire.
 *
 * Une petite fenetre au centre de l'ecran, et rien d'autre : la feuille
 * d'actions se referme d'abord, pour ne pas empiler deux couches sur une
 * seule question. La question reste courte — le libelle de l'action vient
 * d'etre lu, le repeter en entier n'apprend rien.
 */

/** Ce qu'on attend d'un appui sur "Oui". */
interface Pending {
  question: string;
  run: () => void;
}

export function useConfirm(): {
  /** La question en attente, a passer a `ConfirmDialog`. */
  pending: Pending | null;
  /** Arme la question ; rien ne partira avant le "Oui". */
  request: (question: string, run: () => void) => void;
  cancel: () => void;
  confirm: () => void;
} {
  const [pending, setPending] = useState<Pending | null>(null);

  const request = useCallback((question: string, run: () => void) => {
    setPending({ question, run });
  }, []);

  const cancel = useCallback(() => setPending(null), []);

  /*
    L'action part d'ici, et non depuis la fonction de mise a jour.

    Elle y etait : `setPending((current) => { current?.run(); return null })`.
    Une fonction de mise a jour doit etre pure — React se reserve de la
    rejouer, et le mode strict le fait exprement pour reveler celles qui
    ne le sont pas. Le geste confirme partait donc deux fois : deux
    suppressions, deux retraits, deux notes. En production le second
    passage n'avait pas lieu, ce qui rendait la chose invisible jusqu'a
    ce qu'une ecriture refuse d'etre faite deux fois.
  */
  const confirm = useCallback(() => {
    if (!pending) return;
    setPending(null);
    pending.run();
  }, [pending]);

  return { pending, request, cancel, confirm };
}

/**
 * La fenetre elle-meme.
 * Elle vit dans la fente `overlay` des gabarits d'ecran, comme les
 * feuilles d'actions : posee dans le contenu defilant, elle s'ouvrirait
 * sous le pli.
 */
export function ConfirmDialog({
  pending,
  onCancel,
  onConfirm,
}: {
  pending: Pending | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!pending) return null;

  return (
    <div className="confirm-root">
      <button className="confirm__scrim" aria-label={t.commun.annuler} onClick={onCancel} />
      <div className="confirm" role="dialog" aria-modal="true">
        <p className="confirm__question">{tightPunctuation(pending.question)}</p>
        <div className="confirm__actions">
          <button className="confirm__no" onClick={onCancel}>
            {t.commun.annuler}
          </button>
          <button className="confirm__yes" onClick={onConfirm}>
            {t.commun.oui}
          </button>
        </div>
      </div>
    </div>
  );
}
